import { parseTimestamp, TimestampParseError } from "./schema/index.js";
import { step } from "./trace.js";
function fail(message, detail) {
    throw Object.assign(new Error(message), detail);
}
/**
 * The whole script as an ordered timeline. Addresses match what the CLI has
 * always emitted: v4 action ids, and `ep/scn#idx` for v3.
 */
export function spanPoints(doc) {
    return doc.episodes.flatMap((ep) => ep.scenes.flatMap((scene) => scene.actions.map((a, index) => ({
        address: doc.version === 4 ? a.anchor : `${ep.id}/${scene.id}#${index}`,
        episodeId: ep.id,
        episodeNo: ep.no,
        timestamp: a.ts,
    }))));
}
function stamp(point) {
    try {
        return parseTimestamp(point.timestamp ?? "");
    }
    catch (error) {
        if (!(error instanceof TimestampParseError))
            throw error;
        return fail(`Action ${point.address} has an invalid stored timestamp.`, {
            code: "ACTION_SPAN_TIMESTAMP_INVALID",
            hint: `Fix it with \`scriptctl edit ${point.address} --ts <time>\` or remove it.`,
            data: {
                expected: ["valid action.extend.timestamp"],
                actual: [`${point.address}: ${JSON.stringify(point.timestamp ?? "")}`],
            },
        });
    }
}
function nonMonotonic(previous, current) {
    return fail("Action timestamps run backward within the selected episode.", {
        code: "ACTION_SPAN_TIMESTAMP_NON_MONOTONIC",
        hint: "Fix the stored timestamps so they do not move backward in document order, then rerun `span`.",
        data: { actual: [`${previous.address}: ${previous.timestamp}`, `${current.address}: ${current.timestamp}`] },
    });
}
export function resolveSpan(timeline, selectedAddresses) {
    if (selectedAddresses.length === 0) {
        fail("The action selection matched nothing.", {
            code: "ACTION_SPAN_NO_MATCH",
            hint: "Check the selector with `scriptctl actions` and rerun with `--span`.",
            data: { actual: ["0 matched actions"] },
        });
    }
    const wanted = new Set(selectedAddresses);
    const selected = timeline.filter((point) => wanted.has(point.address));
    if (selected.length !== wanted.size) {
        fail("The resolved action selection is inconsistent with the script timeline.", {
            code: "ACTION_SPAN_SELECTION_INCONSISTENT",
            data: {
                expected: [`${wanted.size} timeline actions`],
                actual: [`${selected.length} timeline actions`],
            },
        });
    }
    const episodeIds = new Set(selected.map((point) => point.episodeId));
    if (episodeIds.size !== 1) {
        fail("An action timestamp span must stay within one episode.", {
            code: "ACTION_SPAN_CROSSES_EPISODES",
            hint: "Split the selector into one `span` call per episode.",
            data: { actual: [...episodeIds] },
        });
    }
    const first = selected[0], last = selected[selected.length - 1];
    if (!first.timestamp) {
        fail(`The first selected Action ${first.address} has no timestamp.`, {
            code: "ACTION_SPAN_START_TIMESTAMP_MISSING",
            hint: `Set it with \`scriptctl timestamp ${first.address} --set <time>\`, then rerun.`,
            data: { actual: [first.address] },
        });
    }
    const start = stamp(first);
    let prevPoint = first, prev = start, lastSelected = "";
    for (const point of selected) {
        if (!point.timestamp)
            continue;
        const ts = stamp(point);
        if (ts.milliseconds < prev.milliseconds)
            nonMonotonic(prevPoint, point);
        prevPoint = point;
        prev = ts;
        if (point.address === last.address)
            lastSelected = ts.canonical;
    }
    // The exclusive end is the next *later* timestamp still inside this episode;
    // equal neighbours are legal and do not close the window.
    const lastIndex = timeline.findIndex((point) => point.address === last.address);
    let boundary;
    let boundaryTs;
    for (let i = lastIndex + 1; i < timeline.length; i += 1) {
        const candidate = timeline[i];
        if (candidate.episodeId !== first.episodeId)
            break;
        if (!candidate.timestamp)
            continue;
        const ts = stamp(candidate);
        if (ts.milliseconds < prev.milliseconds)
            nonMonotonic(prevPoint, candidate);
        if (ts.milliseconds === prev.milliseconds)
            continue;
        boundary = candidate;
        boundaryTs = ts;
        break;
    }
    step("span", `${first.episodeId} ${start.canonical}..${boundaryTs?.canonical ?? "(open)"}`
        + ` over ${selected.length} action(s)`);
    return {
        episode_id: first.episodeId,
        ep_no: first.episodeNo,
        start_timestamp: start.canonical,
        last_selected_timestamp: lastSelected,
        end_timestamp: boundaryTs?.canonical ?? "",
        end_open: boundaryTs === undefined,
        end_exclusive: true,
        start_action: first.address,
        last_action: last.address,
        end_boundary_action: boundary?.address ?? "",
        matched_actions: selected.length,
    };
}
export const SPAN_COLUMNS = [
    "episode_id", "ep_no", "start_timestamp", "last_selected_timestamp",
    "end_timestamp", "end_open", "end_exclusive", "start_action", "last_action",
    "end_boundary_action", "matched_actions",
];
/** Timeline addresses for the actions a caller already selected. */
export function spanAddresses(doc, actions) {
    return actions.map((a) => {
        if (doc.version === 4)
            return a.anchor;
        const index = a.scene.actions.indexOf(a);
        return `${a.scene.ep.id}/${a.scene.id}#${index}`;
    });
}
